import { useSuspenseQuery } from "@tanstack/react-query";
import { AnalyticsScreenProvider, Callout, Flex, Link, Text, OnboardingLayout } from "@wayflyer/flyui";
import { IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getStageCompletionStatusQueryOptions, useInviteModalActions } from "shared-ui";
import { handleContactTeam } from "shared-utils";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { MESSAGES } from "../../components/bank-controller-next/message-definitions";
import { BankControllerNext } from "../../components";
import { IncompleteStepsModal } from "../../components/incomplete-steps-modal";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../hooks/navigation-next";
import { useApplicationSubmit } from "../../hooks/use-application-submit";

import { BankControllerProvider, useBankControllerContext } from "./hooks/bank-controller-context";

const BankingNextContent = () => {
  const intl = useIntl();

  const { uploadsInProgress, ready, isSubmitting, handleNext, stepCompleted, isUkIncorporated } =
    useBankControllerContext();
  const { openInviteModal } = useInviteModalActions();
  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.BANKING });
  const applicationSubmit = useApplicationSubmit({ currentPage: PAGE_TYPE.BANKING });

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: stageCompletionStatus } = useSuspenseQuery(getStageCompletionStatusQueryOptions(customerDomain));

  const tradingHistoryThresholdMonths = stageCompletionStatus.timeInBusinessThresholdMonths ?? 6;
  const isLowTimeInBusiness = stageCompletionStatus.isLowTimeInBusiness === true;

  const onNext = () => handleNext(() => applicationSubmit.submitApplication());

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.pageTitle)}
        subtitle={intl.formatMessage(MESSAGES.pageSubtitle)}
        footer={
          <Flex direction="column" gap="8">
            <Flex direction="column" gap="2">
              <Text fontWeight="semiBold" size="xs">
                {intl.formatMessage(MESSAGES.needHelpHeading)}
              </Text>
              <Text size="xs">
                <FormattedMessage
                  {...MESSAGES.footerSupportText}
                  values={{
                    a1: (chunks: ReactNode) => <Link onClick={() => openInviteModal()}>{chunks}</Link>,
                    a2: (chunks: ReactNode) => <Link onClick={handleContactTeam}>{chunks}</Link>,
                  }}
                />
              </Text>
            </Flex>
            {isUkIncorporated && stepCompleted && (
              <Text size="xs">
                <FormattedMessage
                  {...MESSAGES.ukBankingSatisfiedDisclosure}
                  values={{
                    nextStep: navigationContext.nextButtonLabel,
                    privacyLink: (chunks: ReactNode) => (
                      <Link url="https://www.wayflyer.com/privacy-notice" external>
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </Text>
            )}
          </Flex>
        }
        actions={{
          primary: {
            content: navigationContext.nextButtonLabel,
            onAction: onNext,
            disabled: uploadsInProgress || !ready,
            loading: isSubmitting || applicationSubmit.isSubmitting,
            icon: IconArrowRight24Line,
            analyticsId: "bank-controller-next-button",
          },
          // TODO: Add back button
          // secondary: {
          //   content: isMobile ? undefined : intl.formatMessage(COMMON_MESSAGES.backLabel),
          //   icon: isMobile ? IconArrowLeft24Line : undefined,
          //   onAction: handleBack,
          //   analyticsId: "bank-controller-back-button",
          // },
        }}
      >
        {isLowTimeInBusiness && (
          <Callout
            variant="warning"
            description={intl.formatMessage(MESSAGES.timeInBusinessBanner, {
              months: tradingHistoryThresholdMonths,
              b: (chunks: ReactNode) => (
                <Text element="span" fontWeight="semiBold">
                  {chunks}
                </Text>
              ),
            })}
          />
        )}
        <BankControllerNext />
      </OnboardingLayout>
    </>
  );
};

export const BankingNextPage = () => (
  <AnalyticsScreenProvider screen="onboarding-banking">
    <BankControllerProvider>
      <BankingNextContent />
    </BankControllerProvider>
  </AnalyticsScreenProvider>
);
